import React, { useEffect, useRef, useState, memo } from "react";
import {
  MenuItem,
  ListItemIcon,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  Paper,
  Dialog,
  DialogContent,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  getActionMenuListContainerSx,
  getActionMenuPaperSx,
  getActionMenuToggleRowSx,
  getLayoutOptionActionRowSx,
} from "../utils/actionMenuStyles";
import ActionMenuDividerLine from "./GlobalStyles/ActionMenuDividerLine";
import CloseIcon from "@mui/icons-material/Close";
import DoneOutlinedIcon from "@mui/icons-material/DoneOutlined";
import LabeledToggleRow from "./GlobalStyles/LabeledToggleRow";
import { getStandardDialogPaperSx, standardDialogBackdropSx } from "./StandardDialog";

import { baseUrl } from "../utils/baseUrl";
import { useGlobalAuthContext } from "../context/AuthContext";
import axios from "axios";

const ToggleRow = ({
  label,
  subtitle,
  initialChecked,
  onToggle,
  disabled,
  isFloorPlanDialog,
  uniformGutter,
  suppressHover,
}) => {
  const theme = useTheme();
  const [checked, setChecked] = useState(initialChecked);
  return (
    <MenuItem
      sx={getActionMenuToggleRowSx(theme, {
        isFloorPlanDialog,
        uniformGutter,
        suppressHover,
      })}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        setChecked((prev) => !prev);
        onToggle();
      }}
    >
      <LabeledToggleRow
        title={label}
        subtitle={subtitle}
        checked={checked}
        disabled={disabled}
        sx={{ pointerEvents: 'none' }}
      />
    </MenuItem>
  );
};

const LayoutOptionMenu = memo(
  ({
    refetch,
    optionsMenu,
    layoutData,
    onClose,
    headerOffset = 0,
    presentation = "embedded",
    open = true,
    /** `dropdown` (default), `bottomSheet` (gallery strip), or `transparent` when parent shell already styles the surface */
    paperVariant = "dropdown",
    /** When true: 4px (0.5 theme unit) symmetric list gutter + full-width rows (editor + share shell). */
    menuUniformSpacing = false,
    /** Optional small caption rendered under the last row (e.g. editor save nudge). */
    footerNote = null,
  }) => {
    const theme = useTheme();
    const { currentUserSessionStorage } = useGlobalAuthContext();
    const [isNameEditable, setIsNameEditable] = useState(false);
    const layoutNameRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [previosLoading, setPreviosLoading] = useState(false);
    const loadingTimeoutRef = useRef(null);
    const [shareCopiedIndex, setShareCopiedIndex] = useState(null);
    const shareFeedbackTimeoutRef = useRef(null);
    const isFloorPlanDialog = presentation === "dialog";
    /** Editor + share shell: match `/main` floor-plan overflow dialog chrome (pill title, gutters, row gap). */
    const matchDashboardOverflowChrome = menuUniformSpacing && !isFloorPlanDialog;
    const nameFieldLikeFloorPlanDialog = isFloorPlanDialog || matchDashboardOverflowChrome;

    const nameEditDone = async () => {
      if (!layoutNameRef.current) return;

      const updatedName = layoutNameRef.current.innerText;
      try {
        await axios.put(
          `${baseUrl}/savedLayout/edit/${layoutData?._id}`,
          {
            name: updatedName,
          },
          {
            headers: {
              Authorization: `Bearer ${currentUserSessionStorage}`,
            },
          }
        );
        setIsNameEditable(false);
        refetch();
      } catch (error) {
        console.error("Failed to update layout name:", error);
        setIsNameEditable(false);
      }
    };

    useEffect(() => {
      if (isNameEditable && layoutNameRef.current) {
        layoutNameRef.current.focus();
        try {
          const range = document.createRange();
          const selection = window.getSelection();
          if (selection && layoutNameRef.current) {
            range.selectNodeContents(layoutNameRef.current);
            selection.removeAllRanges();
            selection.addRange(range);
          }
        } catch (error) {
          console.error("Failed to select text:", error);
        }
      }
    }, [isNameEditable]);

    useEffect(() => {
      setShareCopiedIndex(null);
    }, [layoutData?._id]);

    useEffect(() => {
      return () => {
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current);
        }
        if (shareFeedbackTimeoutRef.current) {
          clearTimeout(shareFeedbackTimeoutRef.current);
        }
      };
    }, []);

    const nameToolbar = (
      <Box
        sx={{
          display: "flex",
          gap: 1,
          width: "100%",
          alignItems: "center",
          minWidth: 0,
        }}
      >
        <Typography
          ref={layoutNameRef}
          contentEditable={isNameEditable}
          suppressContentEditableWarning
          onClick={() => {
            setIsNameEditable(true);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              nameEditDone();
            }
          }}
          sx={
            nameFieldLikeFloorPlanDialog
              ? {
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  py: 0.875,
                  px: 1.125,
                  minWidth: 0,
                  flex: 1,
                  minHeight: 40,
                  display: "flex",
                  alignItems: "center",
                  borderRadius: "9999px",
                  fontWeight: 500,
                  fontSize: "1.0625rem",
                  lineHeight: 1.45,
                  boxSizing: "border-box",
                  outline: "none",
                  mt: 0,
                  cursor: "pointer",
                  ...(isNameEditable
                    ? {
                        boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.primary.main, 0.45)}`,
                        backgroundColor: alpha(theme.palette.common.white, 0.95),
                        "&:hover": {
                          backgroundColor: alpha(theme.palette.common.white, 0.95),
                        },
                      }
                    : {
                        "&:hover": {
                          backgroundColor: alpha(theme.palette.grey[200], 0.55),
                        },
                      }),
                }
              : {
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  py: 0.75,
                  px: 1,
                  minWidth: 0,
                  flex: 1,
                  borderRadius: "10px",
                  fontWeight: 500,
                  fontSize: "1.0625rem",
                  lineHeight: 1.35,
                  ...(isNameEditable
                    ? {
                        outline: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
                        backgroundColor: alpha(theme.palette.common.white, 0.65),
                      }
                    : {}),
                }
          }
        >
          {layoutData?.name || (isNameEditable ? "\u00A0" : "Untitled")}
        </Typography>

        {isNameEditable && (
          <IconButton
            size="small"
            onClick={nameEditDone}
            sx={{ flexShrink: 0 }}
          >
            <DoneOutlinedIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    );

    const closeButton = (
      <IconButton
        aria-label="Close menu"
        size="small"
        onClick={onClose}
        sx={{
          flexShrink: 0,
          alignSelf: "center",
          color: "text.secondary",
          "&:hover": { bgcolor: alpha(theme.palette.action.hover, 0.08) },
        }}
      >
        <CloseIcon sx={{ fontSize: 22 }} />
      </IconButton>
    );

    const rowCtx = { isFloorPlanDialog, uniformGutter: menuUniformSpacing };
    const actionRowSx = (option) => getLayoutOptionActionRowSx(theme, option, rowCtx);

    const menuList = (
      <Box
        sx={getActionMenuListContainerSx(presentation, {
          uniformSpacing: menuUniformSpacing,
        })}
      >
        {optionsMenu?.map((option, index) => {
          if (option?.toHide?.() === true) return null;
          if (option?.share) {
            const shareCopied = shareCopiedIndex === index;
            return (
              <MenuItem
                key={`share-${index}`}
                sx={actionRowSx(option)}
                onClick={async (e) => {
                  e.stopPropagation();
                  const galleryUrl = `${window.location.origin}/floor-gallery/${layoutData?._id}`;
                  try {
                    await navigator.clipboard.writeText(galleryUrl);
                    setShareCopiedIndex(index);
                    if (shareFeedbackTimeoutRef.current) {
                      clearTimeout(shareFeedbackTimeoutRef.current);
                    }
                    shareFeedbackTimeoutRef.current = setTimeout(
                      () => setShareCopiedIndex(null),
                      2000
                    );
                  } catch (err) {
                    console.warn("Clipboard write failed:", err);
                  }
                }}
              >
                {shareCopied ? "Link copied!" : "Share"}
              </MenuItem>
            );
          }

          const dividerBefore = !isFloorPlanDialog && option.divider === true;

          if (option.reminder) {
            return (
              <React.Fragment key={index}>
                {dividerBefore && <ActionMenuDividerLine />}
                <MenuItem
                  sx={{
                    ...actionRowSx(option),
                    pointerEvents: "none",
                    color: "text.secondary",
                    opacity: 0.85,
                    "&:hover": { backgroundColor: "transparent" },
                  }}
                  disableRipple
                >
                  {option?.icon && (
                    <ListItemIcon sx={{ minWidth: 36, color: "inherit" }}>
                      {option.icon}
                    </ListItemIcon>
                  )}
                  {option.label}
                </MenuItem>
              </React.Fragment>
            );
          }

          return (
            <React.Fragment key={index}>
              {dividerBefore && <ActionMenuDividerLine />}
              {option.isToggle ? (
                <ToggleRow
                  label={option.label}
                  subtitle={option.toggleSubtitle}
                  initialChecked={option.toggleChecked}
                  onToggle={option.value}
                  disabled={!!loading}
                  isFloorPlanDialog={isFloorPlanDialog || menuUniformSpacing}
                  uniformGutter={menuUniformSpacing}
                  suppressHover={option.suppressToggleHover}
                />
              ) : (
                <MenuItem
                  sx={actionRowSx(option)}
                  disabled={!!loading}
                  onClick={async () => {
                    setLoading(`${index}-${option?.label}`);
                    try {
                      await option?.value();
                      setPreviosLoading(`${index}-${option?.label}`);
                    } catch (error) {
                      console.error(`Failed to execute ${option?.label}:`, error);
                    } finally {
                      setLoading(null);
                      if (loadingTimeoutRef.current) {
                        clearTimeout(loadingTimeoutRef.current);
                      }
                      loadingTimeoutRef.current = setTimeout(() => setPreviosLoading(null), 2000);
                    }
                  }}
                >
                  {option?.icon && (
                    <ListItemIcon>
                      {previosLoading === `${index}-${option?.label}` && option?.loadingDoneIcon
                        ? option.loadingDoneIcon
                        : option.icon}
                    </ListItemIcon>
                  )}
                  {loading === `${index}-${option?.label}`
                    ? option?.loadingLabel || option?.label
                    : previosLoading === `${index}-${option?.label}`
                      ? option?.loadingDoneLabel || option?.label
                      : option?.label}

                  {loading === `${index}-${option?.label}` && (
                    <CircularProgress size={12} sx={{ marginLeft: 2 }} />
                  )}

                  <Box
                    sx={{
                      position: "absolute",
                      right: "16px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {option?.rightIcon}
                  </Box>
                </MenuItem>
              )}

              {option?.helperText && (
                <p
                  style={{
                    paddingBottom: "8px",
                    textAlign: "center",
                    margin: "0 16px",
                    marginTop: "3px",
                  }}
                >
                  {option?.helperText}
                </p>
              )}
            </React.Fragment>
          );
        })}

        {footerNote && (
          <Typography
            sx={{
              px: 1.75,
              fontSize: "11px",
              lineHeight: 1.35,
              color: "text.secondary",
              textAlign: "center",
            }}
          >
            {footerNote}
          </Typography>
        )}
      </Box>
    );

    if (presentation === "dialog") {
      return (
        <Dialog
          open={open}
          onClose={onClose}
          maxWidth={false}
          fullWidth={false}
          scroll="paper"
          aria-label="Floor plan options"
          BackdropProps={{ sx: standardDialogBackdropSx }}
          PaperProps={{
            sx: {
              ...getStandardDialogPaperSx(theme),
              overflow: "hidden",
              width: { xs: "min(320px, calc(100vw - 24px))", sm: 360 },
              maxWidth: "calc(100vw - 16px)",
              margin: { xs: "12px", sm: "16px" },
              maxHeight: "min(72vh, 480px)",
            },
          }}
        >
          <DialogContent
            sx={{
              p: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              boxSizing: "border-box",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 0.5,
                px: 0.75,
                pt: 0.75,
                pb: 0.75,
                minWidth: 0,
                boxSizing: "border-box",
                borderBottom: `1px solid ${alpha(theme.palette.common.black, 0.08)}`,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", overflow: "hidden" }}>
                {nameToolbar}
              </Box>
              {closeButton}
            </Box>
            <Box
              sx={{
                px: 0.75,
                pt: 0.75,
                pb: 0.75,
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                boxSizing: "border-box",
              }}
            >
              {menuList}
            </Box>
          </DialogContent>
        </Dialog>
      );
    }

    return (
      <Box sx={{ width: "100%" }}>
        <Paper
          elevation={0}
          sx={{
            ...getActionMenuPaperSx(theme, paperVariant),
            p: 0,
            overflow: "hidden",
            margin: 0,
            maxWidth: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              ...(menuUniformSpacing
                ? {
                    gap: 0.5,
                    px: 0.75,
                    pt: 0.75,
                    pb: 0.75,
                  }
                : {
                    gap: 1,
                    pl: 2.5,
                    pr: 1.5,
                    pt: headerOffset ? `${headerOffset}px` : 2.5,
                    pb: 1.5,
                  }),
              borderBottom: `1px solid ${alpha(theme.palette.common.black, 0.08)}`,
              minWidth: 0,
              boxSizing: "border-box",
            }}
          >
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                alignItems: "center",
                overflow: "hidden",
              }}
            >
              {nameToolbar}
            </Box>
            {closeButton}
          </Box>

          {menuUniformSpacing ? (
            <Box
              sx={{
                px: 0.75,
                pt: 0.75,
                pb: 0.75,
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              {menuList}
            </Box>
          ) : (
            menuList
          )}
        </Paper>
      </Box>
    );
  },
  (prevProps, nextProps) =>
    prevProps.optionsMenu === nextProps.optionsMenu &&
    prevProps.layoutData?._id === nextProps.layoutData?._id &&
    prevProps.layoutData?.name === nextProps.layoutData?.name &&
    prevProps.headerOffset === nextProps.headerOffset &&
    prevProps.presentation === nextProps.presentation &&
    prevProps.open === nextProps.open &&
    prevProps.paperVariant === nextProps.paperVariant &&
    prevProps.menuUniformSpacing === nextProps.menuUniformSpacing &&
    prevProps.footerNote === nextProps.footerNote
);

LayoutOptionMenu.displayName = "LayoutOptionMenu";

export default LayoutOptionMenu;
